/**
 * bb-browse-mega.js — BROWSE mega menu rail/pane switching.
 * Hovering, focusing or (on touch) tapping a left-rail parent link reveals its
 * pane on the right. Clicking a rail link still navigates to that parent
 * collection. No deps.
 *
 * Serves two callers that share the `.bb-browse` markup:
 *   - snippets/bb-header-search.liquid — a <details> in the search bar
 *   - snippets/bb-mega-panel.liquid    — a <div> inside a header-menu dropdown
 */

/**
 * Wired nodes, tracked separately at each level.
 *
 * WeakSets rather than data-attribute flags because Horizon morphs sections in
 * place (assets/morph.js): a morph copies attributes from freshly rendered HTML,
 * which would clear an attribute flag while the live listeners survived, and
 * re-running init() would then double them up. Identity keyed off the element
 * object has no such hole, and entries are collected once a node is dropped.
 *
 * The RAIL ITEMS are tracked separately from their container on purpose. A morph
 * keeps the container (same tag, no key) while replacing what is inside it, so a
 * container-level guard alone would see "already wired" and skip rail items that
 * had just been added — a merchant adding a menu link would get a permanently
 * dead item until the next full page load.
 *
 * @type {WeakSet<Element>}
 */
const wiredContainers = new WeakSet();
/** @type {WeakSet<Element>} */
const wiredItems = new WeakSet();

/** True when the primary pointer cannot hover — i.e. tap, not mouse. */
const isCoarsePointer = () => window.matchMedia('(hover: none)').matches;

/**
 * Re-measure the enclosing header dropdown after a pane swap.
 *
 * header-menu.js measures `submenu.offsetHeight` ONCE, when the top-level item
 * activates, and freezes it into --submenu-height / --full-open-header-height.
 * Those drive both the panel's height and the clip-path in _header-menu.liquid:518.
 * Switching rail panes changes the submenu's height without going through that
 * code, so a taller pane stayed clipped at the first pane's height: everything
 * below the original bottom edge was invisible AND unclickable, with no
 * scrollbar and no way to reach the rest of the category.
 *
 * headerVisibleHeight is DERIVED, not recomputed. header-menu.js set
 * full-open = submenu + headerVisible, so the difference between the two values
 * already on the element is that term. Re-deriving it would mean duplicating
 * #getHeaderVisibleHeight(), including its data-submenu-overlap-bottom-row
 * branch — a private method this file must not fork, because the copy would
 * silently drift the first time Horizon changed it.
 *
 * No-ops for the search-bar <details> caller, which has no .menu-list__submenu
 * ancestor and is sized by its own CSS.
 *
 * @param {Element} browse
 */
function resyncSubmenuHeight(browse) {
  const submenu = browse.closest('.menu-list__submenu');
  if (!(submenu instanceof HTMLElement)) return;

  const header = document.querySelector('#header-component');
  if (!(header instanceof HTMLElement)) return;

  // After layout, or offsetHeight still reports the outgoing pane's box.
  requestAnimationFrame(() => {
    const styles = getComputedStyle(header);
    const prevSubmenu = parseFloat(styles.getPropertyValue('--submenu-height')) || 0;
    const prevFull = parseFloat(styles.getPropertyValue('--full-open-header-height')) || 0;

    // Both zero = the dropdown is closed or closing. That state belongs to
    // header-menu.js (#deactivate sets exactly this); writing a height here
    // would prop the header open with nothing in it.
    if (prevSubmenu === 0 && prevFull === 0) return;

    const headerVisibleHeight = Math.max(0, prevFull - prevSubmenu);
    const nextSubmenu = submenu.offsetHeight;
    if (!nextSubmenu || nextSubmenu === prevSubmenu) return;

    header.style.setProperty('--submenu-height', `${nextSubmenu}px`);
    header.style.setProperty('--full-open-header-height', `${nextSubmenu + headerVisibleHeight}px`);
  });
}

/**
 * Reveal one pane and mark its rail item active.
 *
 * Queries the rail and panes live on every call instead of closing over arrays
 * captured at wire time — after a morph those arrays would point at detached
 * nodes, and already-wired items would go on toggling the old list. The cost is
 * two querySelectorAll over a menu-sized subtree per hover.
 *
 * @param {Element} browse
 * @param {string | undefined} id
 */
function activate(browse, id) {
  if (!id) return;
  for (const r of browse.querySelectorAll('.bb-browse__rail-item')) {
    r.classList.toggle('is-active', /** @type {HTMLElement} */ (r).dataset.megaTarget === id);
  }
  for (const p of browse.querySelectorAll('.bb-browse__pane')) {
    p.classList.toggle('is-active', /** @type {HTMLElement} */ (p).dataset.megaPane === id);
  }

  syncRailFocus(browse, id);

  // The pane that just became active may be taller or shorter than the one it
  // replaced; the enclosing dropdown was measured before this call.
  resyncSubmenuHeight(browse);
}

/** Rail items of one .bb-browse, in DOM order. @param {Element} browse */
function railItems(browse) {
  return /** @type {HTMLElement[]} */ ([...browse.querySelectorAll('.bb-browse__rail-item')]);
}

/**
 * Roving tabindex + aria state across the rail.
 *
 * THE BUG THIS FIXES. Panes follow ALL rail items in DOM order, and an inactive
 * pane is display:none (bb-header.css:352), so its links are not focusable.
 * Activation used to run on `focus`, which meant Tab from a rail item moved to
 * the NEXT rail item and, in doing so, hid the pane the user was about to enter.
 * Every pane except the last was therefore unreachable by keyboard — you could
 * never Tab into one, because arriving at the next rail item destroyed it.
 *
 * With one tabbable rail item, Tab leaves the rail entirely and lands in the
 * active pane's own links, which is the behaviour a keyboard user expects.
 * Arrow keys move within the rail, which is what the focus handler used to do
 * implicitly and far too eagerly.
 *
 * @param {Element} browse
 * @param {string | undefined} id
 */
function syncRailFocus(browse, id) {
  for (const item of railItems(browse)) {
    const isCurrent = item.dataset.megaTarget === id;
    item.setAttribute('aria-expanded', isCurrent ? 'true' : 'false');
    item.tabIndex = isCurrent ? 0 : -1;
  }
}

/**
 * @param {Element} browse
 * @param {HTMLElement} item
 */
function wireRailItem(browse, item) {
  if (wiredItems.has(item)) return;
  wiredItems.add(item);

  // Establish the roving tabindex the first time any item in this rail is wired.
  // Deliberately NOT emitted by Liquid: without JS there is no arrow-key
  // handler, so a rendered tabindex="-1" would strip every rail link but the
  // first out of the tab order and leave them unreachable. Rendered markup stays
  // fully navigable; the roving behaviour only appears once its keyboard
  // handling does.
  const current = browse.querySelector('.bb-browse__rail-item.is-active');
  syncRailFocus(browse, /** @type {HTMLElement | null} */ (current)?.dataset.megaTarget);

  const act = () => activate(browse, item.dataset.megaTarget);

  // Hover-reveal is for pointers that can actually hover. On touch, a tap fires
  // pointerenter during touchstart — before click — so leaving this unguarded
  // meant act() had already set is-active by the time the click handler ran, its
  // "already active?" check passed, and the first tap both revealed the pane AND
  // followed the link. Gating here is what makes the two-tap behaviour below
  // real rather than merely intended.
  item.addEventListener('pointerenter', () => {
    if (!isCoarsePointer()) act();
  });

  // NO `focus` handler. Activating on focus is what made every pane but the last
  // keyboard-unreachable — see syncRailFocus. Selection now moves on arrow keys,
  // the standard way to move within a composite widget, and Tab is left free to
  // do the thing the user actually wants: leave the rail and enter the pane.
  item.addEventListener('keydown', (event) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const items = railItems(browse);
    const index = items.indexOf(item);
    if (index === -1) return;

    // Only claim the key once we know we can act on it, so an unexpected DOM
    // shape falls through to the browser instead of swallowing the keystroke.
    event.preventDefault();

    let next;
    if (event.key === 'Home') next = items[0];
    else if (event.key === 'End') next = items[items.length - 1];
    else if (event.key === 'ArrowDown') next = items[(index + 1) % items.length];
    else next = items[(index - 1 + items.length) % items.length];

    activate(browse, next.dataset.megaTarget);
    next.focus();
  });

  // Touch has no hover state to reveal the pane with, so a tap used to just
  // navigate away to the parent collection and the pane was never seen. First
  // tap reveals, second tap follows the link. Checked per-event rather than once
  // at load so it tracks a device whose pointer type changes (2-in-1 laptops,
  // responsive-mode testing).
  //
  // Keyboard Enter is unaffected: there is no `focus` handler (see
  // syncRailFocus above), and the guard below returns for any non-coarse
  // pointer before it ever checks is-active, so the click this synthesizes
  // hits neither preventDefault() nor act() and the link just navigates.
  item.addEventListener('click', (event) => {
    if (!isCoarsePointer()) return;
    if (item.classList.contains('is-active')) return;
    event.preventDefault();
    act();
  });
}

/** @param {Element} browse */
function wireContainer(browse) {
  if (wiredContainers.has(browse)) return;
  wiredContainers.add(browse);

  // Only <details> has an `open` property to close; the inline dropdown panels
  // are plain divs whose open/close Horizon's own menu handling owns.
  if (!(browse instanceof HTMLDetailsElement)) return;

  // A11y: Escape closes the open mega and returns focus to its summary so a
  // keyboard user is never trapped in the viewport-spanning disclosure.
  browse.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !browse.open) return;
    browse.removeAttribute('open');
    const summary = browse.querySelector('summary');
    if (summary instanceof HTMLElement) summary.focus();
  });

  // Closing leaves `is-active` on whichever pane was last revealed, so the next
  // open flashes a stale pane before the first hover corrects it. Snap back to
  // the first rail item on close — read live, since the rail may have changed.
  browse.addEventListener('toggle', () => {
    if (browse.open) return;
    const first = /** @type {HTMLElement | null} */ (browse.querySelector('.bb-browse__rail-item'));
    activate(browse, first?.dataset.megaTarget);
  });
}

function init() {
  if (typeof document === 'undefined') return;
  for (const browse of document.querySelectorAll('.bb-browse')) {
    wireContainer(browse);
    for (const item of browse.querySelectorAll('.bb-browse__rail-item')) {
      wireRailItem(browse, /** @type {HTMLElement} */ (item));
    }
  }
}

init();

// Panels and rail items can arrive after this module first evaluates — the theme
// editor re-rendering the header group, or a section morph replacing the nodes.
// Without this, rail/pane switching silently stopped working for the rest of the
// session and only a full reload brought it back. Both wire functions are
// idempotent, so re-running on every section load is free.
document.addEventListener('shopify:section:load', init);
